// ============================================================
// missingLimitation.ts — "this case has no limitation date" (pure;
// missingLimitation.test.ts). The SAME rule as the server
// (backend/src/lib/limitationEstimate.ts MISSING_LIMITATION_WHERE): the case
// isn't closed (drafts count) and it has NO limitation-type deadline at all —
// open or completed. Used by the case list's flag + "No limitation date"
// filter (getCases' hasLimitationDate), the dashboard's hero link (the
// count-only GET /api/cases/missing-limitation?count=1)
// and the case page's required-fields row (the loaded deadlines).
// ============================================================
import type { CaseDeadline } from '../types/deadlines.types'
import { isLimitationName } from './limitationRules'

const isClosed = (closeDate: string | null | undefined): boolean => !!closeDate && closeDate.trim() !== ''

// A case-list row: the server's hasLimitationDate (undefined = not known →
// never flagged).
// (B2b) A child sexual abuse claim confirmed as having no time limit
// (getCases' noTimeLimit) is resolved — never flagged.
export const rowMissingLimitation = (c: { closeDate: string | null; hasLimitationDate?: boolean; noTimeLimit?: boolean }): boolean =>
  c.hasLimitationDate === false && c.noTimeLimit !== true && !isClosed(c.closeDate)

// The open case page: its loaded deadlines (undefined = still loading →
// never flagged, so nothing flickers in). (Audit fix 1) A valid "no time
// limit" confirmation (s.11ZA) is resolved — never flagged, as on the list.
export const caseMissingLimitation = (closeDate: string | null | undefined, deadlines: CaseDeadline[] | undefined, noTimeLimit = false): boolean =>
  deadlines !== undefined && !noTimeLimit && !isClosed(closeDate) && !deadlines.some((d) => isLimitationName(d.deadlineType?.name))

// The case list's place param for the filter (?view=cases&limitation=missing —
// also what the daily nudge's email and bell link to).
export const NO_LIMITATION_PARAM = 'limitation'
export const NO_LIMITATION_VALUE = 'missing'

// The case list's "No limitation date (N)" for its assignee filter, from the
// GET /api/cases/missing-limitation rows fetched WITH the list's own server
// assignee param ('me' / a user id → owner OR handling team; 'all' and
// 'unassigned' → none). 'unassigned' is then narrowed here exactly as the
// list narrows its rows (no owner). null = not loaded → no option.
export const noLimitationOptionCount = (rows: { assignedToUserId: string | null }[] | null | undefined, assigneeFilter: string): number | null =>
  rows ? (assigneeFilter === 'unassigned' ? rows.filter((r) => r.assignedToUserId === null) : rows).length : null

// ── Arriving from the daily nudge / dashboard links (CaseListScreen) ──
// ★ (Audit) Opened on "No limitation date" → status is "all": no closed case
// is ever on that filter, so a remembered "Closed" showed an EMPTY list (and
// "Draft"/"Open" part of it). Otherwise the URL's status, else the remembered one.
export const initialStatusFilterFor = <S extends string>(
  place: { limitation: string | null; status: string | null },
  stored: string | null,
  isStatus: (v: string | null) => v is S,
): S | 'all' => {
  if (place.limitation === NO_LIMITATION_VALUE) return 'all'
  if (isStatus(place.status)) return place.status
  return isStatus(stored) ? stored : 'all'
}
// A link's one-shot ?assignee= (the nudge email: me / all) wins over the
// remembered filter; default "me".
export const initialAssigneeFilterFor = (fromLink: string | null, stored: string | null): string =>
  fromLink || (typeof stored === 'string' && stored.length > 0 ? stored : 'me')
// The nudge's bell link → the assignee filter to open the list with:
// "no-limitation:me" → me; "no-limitation:all" or a bare (older) "no-limitation"
// → all; anything else → null (not this link).
export const noLimitationLinkAssignee = (linkId: string): 'me' | 'all' | null =>
  linkId === 'no-limitation:me' ? 'me' : linkId === 'no-limitation' || linkId === 'no-limitation:all' ? 'all' : null
